/**
 * Shared table column configurations for PACS and Worklist tables
 * Following DRY principle to avoid code duplication
 */

import { Tag, Button, Tooltip, Popconfirm, Dropdown, Menu, Select } from 'antd';
import React from 'react';

import {
  FileOutlined,
  FileTextOutlined,
  PrinterOutlined,
  UploadOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  ShareAltOutlined,
  EyeOutlined,
  ExportOutlined,
  AlertOutlined,
  FileWordOutlined,
  EditOutlined,
  MoreOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { calculateExactAge, getUserDetails, hasPermission } from './helper';
import moment from 'moment';
import { CENTER_RADIOLOGY_URL } from '../constants/APP_CONSTANTS';
import { FieldLabels, Permissions } from '../constants/fieldLabels';

interface ActionColumnConfig {
  openViewer?: (record: any) => void;
  showOrdDetails?: (record: any) => void;
  openRadDesk?: (record: any) => void;
  openReportEditor: (record: any) => void;
  addFile?: (record: any) => void;
  viewNotes: (record: any) => void;
  printReport?: (record: any) => void;
  downloadDocxReport?: (record: any) => void;
  viewReport?: (record: any) => void;
  editSignedOffReport?: (record: any) => void;
  handleShareStudy?: (record: any) => void;
  handleDownloadStudy?: (record: any) => void;
  editStudyDetails?: (record: any) => void;
  showStudyHistory?: (record: any) => void;
  assignToSelfTechnician?: (record: any, idx: number) => void;
  toggleReporting?: (record: any, idx: number) => void;
  toggleConfirmation?: (record: any, idx: number) => void;
  toggleFeatures?: (record: any, idx: number, feature: string) => void;
  role?: string;
  pageType?: 'pacs' | 'worklist'; // Determines which actions to show
}

const iconStyle = {
  fontSize: '18px',
  cursor: 'pointer',
  color: '#595959',
};

const disabledIconStyle = {
  ...iconStyle,
  cursor: 'not-allowed',
  color: '#d9d9d9',
};

const iconButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px',
  borderRadius: '4px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  minWidth: '28px',
  minHeight: '28px',
};

const iconButtonHoverStyle = {
  ':hover': {
    backgroundColor: '#f0f0f0',
  },
};

const disabledIconButtonStyle = {
  ...iconButtonStyle,
  cursor: 'not-allowed',
  opacity: 0.5,
};

/**
 * Calculate dynamic width for actions column based on number of icons
 * @param config - Action column configuration
 * @returns Width in pixels
 */
export const calculateActionsColumnWidth = (config: Partial<ActionColumnConfig>): number => {
  const isPacsPage = config.pageType === 'pacs';

  if (!isPacsPage) {
    // Worklist page - minimal width
    return 80;
  }

  let iconCount = 0;
  const userDetails = getUserDetails();

  // Count maximum possible icons that could be shown

  // Upload Notes (permission-based)
  if (hasPermission('CREATE_TECH_NOTES') && config.addFile) {
    iconCount++;
  }

  // View Notes (always visible)
  iconCount++;

  // Open Reporting (non-technician)
  if (userDetails?.user_type !== 'technician') {
    iconCount++;
  }

  // Share Study (always available on PACS)
  if (config.handleShareStudy) {
    iconCount++;
  }

  // More Actions dropdown (always visible on PACS)
  iconCount++;

  // Calculate width: (icon_width * count) + (gap * (count - 1)) + padding
  // Each icon button: 28px, gap between icons: 8px (4px margin each side), container padding: 12px (6px left + 6px right)
  const iconWidth = 34;
  const gapWidth = 8;
  const containerPadding = 12;

  const calculatedWidth = iconWidth * iconCount + gapWidth * (iconCount - 1) + containerPadding;

  // Add some buffer for safety (12px)
  return calculatedWidth;
};

/**
 * Get status badge styling
 */
const getStatusStyle = (status: string) => {
  const baseStyle = {
    height: '20px',
    borderRadius: '4px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '20px',
    letterSpacing: '0.06px',
    verticalAlign: 'middle',
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  switch (status) {
    case 'SCANNED':
      return {
        ...baseStyle,
        width: '91px',
        padding: '0 8px',
        background: '#DEF0FF',
        color: '#003FD2',
      };
    case 'SIGNEDOFF':
      return {
        ...baseStyle,
        width: '104px',
        padding: '0 8px',
        background: '#DAF3EE',
        color: '#00795C',
      };
    case 'DRAFTED':
      return {
        ...baseStyle,
        width: '85px',
        padding: '0 8px',
        background: '#FEECD5',
        color: '#AC5000',
      };
    default:
      return {
        ...baseStyle,
        width: 'auto',
        padding: '0 8px',
        background: '#DEF0FF',
        color: '#003FD2',
      };
  }
};

/**
 * Shared action column renderer
 */
export const renderActionColumn = (config: ActionColumnConfig) => {
  const userDetails = getUserDetails();
  const radUrl = CENTER_RADIOLOGY_URL;
  const isPacsPage = config.pageType === 'pacs';

  return (_, record) => {
    const studyUID = record?.ps_study_uid;
    const hasNotes = record?.note_id;
    const pacs_status = record?.order_workflow?.ow_pacs_status;
    const canViewReport = pacs_status !== 'PENDING';
    const canPrintReport = pacs_status === 'SIGNEDOFF';

    // Count visible icons for dynamic width
    let visibleIconCount = 0;

    // Upload Notes (permission-based)
    if (isPacsPage && hasPermission('CREATE_TECH_NOTES') && config.addFile) {
      visibleIconCount++;
    }

    // View Notes (always visible)
    visibleIconCount++;

    // Open Reporting (non-technician on PACS)
    if (isPacsPage && userDetails?.user_type !== 'technician') {
      visibleIconCount++;
    }

    // Share Study (if studyUID exists on PACS)
    if (isPacsPage && studyUID && config.handleShareStudy) {
      visibleIconCount++;
    }

    // More Actions (always on PACS)
    if (isPacsPage) {
      visibleIconCount++;
    }

    // Calculate dynamic width
    const iconWidth = 28;
    const gapWidth = 8;
    const containerPadding = 12;
    const containerWidth =
      iconWidth * visibleIconCount +
      gapWidth * Math.max(0, visibleIconCount - 1) +
      containerPadding;

    // Build More Actions dropdown menu
    const moreActionsMenu = (
      <Menu>
        {/* Report Actions Section */}
        {(
          <>
            <Menu.ItemGroup title="Report Actions">
              <Menu.Item
                key="view-report"
                icon={<EyeOutlined />}
                disabled={!canPrintReport}
                onClick={e => {
                  e.domEvent.stopPropagation();
                  if (canPrintReport && config.viewReport) {
                    config.viewReport(record);
                  }
                }}
              >
                View Report
              </Menu.Item>
              <Menu.Item
                key="print-report"
                icon={<PrinterOutlined />}
                disabled={!canPrintReport}
                onClick={e => {
                  e.domEvent.stopPropagation();
                  if (canPrintReport && config.printReport) {
                    config.printReport(record);
                  }
                }}
              >
                Print / Download PDF
              </Menu.Item>
              {(userDetails?.user_type === 'radiologist' ||
                userDetails?.user_type === 'hod' ||
                userDetails?.user_type === 'typist') && (
                <Menu.Item
                  key="download-word"
                  icon={<FileWordOutlined />}
                  disabled={!canPrintReport}
                  onClick={e => {
                    e.domEvent.stopPropagation();
                    if (canPrintReport && config.downloadDocxReport) {
                      config.downloadDocxReport(record);
                    }
                  }}
                >
                  Download Word
                </Menu.Item>
              )}
            </Menu.ItemGroup>
            <Menu.Divider />
          </>
        )}

        {/* Study Actions Section */}
        <Menu.ItemGroup title="Study Actions">
          {config.openViewer && (
            <Menu.Item
              key="open-viewer"
              icon={<EyeOutlined />}
              onClick={e => {
                e.domEvent.stopPropagation();
                config.openViewer(record);
              }}
            >
              Open Viewer
            </Menu.Item>
          )}
          {config.handleDownloadStudy && (
            <Menu.Item
              key="download-study"
              icon={<DownloadOutlined />}
              onClick={e => {
                e.domEvent.stopPropagation();
                config.handleDownloadStudy(record);
              }}
            >
              Download Study
            </Menu.Item>
          )}
          {isPacsPage &&
            hasPermission(Permissions.MODIFY_STUDY_DETAILS) &&
            config.editStudyDetails && (
              <Menu.Item
                key="edit-study"
                icon={<EditOutlined />}
                onClick={e => {
                  e.domEvent.stopPropagation();
                  config.editStudyDetails(record);
                }}
              >
                Edit Study Details
              </Menu.Item>
            )}
        </Menu.ItemGroup>

        {/* Other Actions Section */}
        {(config.showOrdDetails || (radUrl && config.openRadDesk) || config.showStudyHistory) && (
          <>
            <Menu.Divider />
            <Menu.ItemGroup title="Other Actions">
              {config.showStudyHistory && (
                <Menu.Item
                  key="study-history"
                  icon={<ClockCircleOutlined />}
                  onClick={e => {
                    e.domEvent.stopPropagation();
                    config.showStudyHistory(record);
                  }}
                >
                  Study History Timeline
                </Menu.Item>
              )}
              {config.showOrdDetails && (
                <Menu.Item
                  key="patient-info"
                  icon={<InfoCircleOutlined />}
                  onClick={e => {
                    e.domEvent.stopPropagation();
                    config.showOrdDetails(record);
                  }}
                >
                  Patient & Order Info
                </Menu.Item>
              )}
              {radUrl && config.openRadDesk && (
                <Menu.Item
                  key="radiology-desk"
                  icon={<ExportOutlined />}
                  onClick={e => {
                    e.domEvent.stopPropagation();
                    config.openRadDesk(record);
                  }}
                >
                  Radiology Desk
                </Menu.Item>
              )}
            </Menu.ItemGroup>
          </>
        )}
      </Menu>
    );

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: `${containerWidth}px`,
          height: '28px',
          borderRadius: '8px',
          paddingTop: '4px',
          paddingRight: '6px',
          paddingBottom: '4px',
          paddingLeft: '6px',
        }}
      >
        {/* ESSENTIAL ACTIONS - Always Visible */}

        {/* Upload Notes/Files - Permission-based access on PACS page */}
        {isPacsPage && hasPermission(Permissions.CREATE_TECH_NOTES) && config.addFile && (
          <Tooltip title="Upload Notes or Files">
            <span
              className="action-icon-button"
              onClick={e => {
                e.stopPropagation();
                config.addFile(record);
              }}
            >
              <UploadOutlined style={iconStyle} />
            </span>
          </Tooltip>
        )}

        {/* View Notes - Always available */}
        <Tooltip title={hasNotes ? 'View Notes' : 'No Notes available'}>
          <span
            className={`action-icon-button ${!hasNotes ? 'disabled' : ''}`}
            onClick={e => {
              e.stopPropagation();
              hasNotes && config.viewNotes(record);
            }}
          >
            <FileOutlined style={hasNotes ? iconStyle : disabledIconStyle} />
          </span>
        </Tooltip>

        {/* Open Reporting - Only on PACS page for non-technicians */}
        {isPacsPage && userDetails?.user_type !== 'technician' && (
          <Tooltip title="Open Reporting">
            <span
              className="action-icon-button"
              onClick={e => {
                e.stopPropagation();
                config.openReportEditor && config.openReportEditor(record);
              }}
            >
              <FileTextOutlined style={iconStyle} />
            </span>
          </Tooltip>
        )}

        {/* Share Study Link - Only on PACS page */}
        {isPacsPage && studyUID && config.handleShareStudy && (
          <Tooltip title="Share Study">
            <span
              className="action-icon-button"
              onClick={e => {
                e.stopPropagation();
                config.handleShareStudy(record);
              }}
            >
              <ShareAltOutlined style={iconStyle} />
            </span>
          </Tooltip>
        )}

        {/* MORE ACTIONS DROPDOWN - Only on PACS page */}
        {isPacsPage && (
          <Dropdown
            overlay={moreActionsMenu}
            trigger={['click']}
            placement="bottomRight"
          >
            <Tooltip title="More Actions">
              <span
                className="action-icon-button"
                onClick={e => e.stopPropagation()}
              >
                <MoreOutlined style={iconStyle} />
              </span>
            </Tooltip>
          </Dropdown>
        )}
      </div>
    );
  };
};

/**
 * Base column definitions shared by both tables
 */
export const getBaseColumns = () => {
  const userDetails = getUserDetails();

  return [
    {
      dataIndex: 'po_pin',
      key: 'po_pin',
      title: FieldLabels.PATIENT_ID,
      width: 105,
      render: (text, rec) => {
        const patPin = rec?.pacs_order?.patient?.pat_pin;
        return (
          <Tooltip title={patPin}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {patPin}
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'po_pat_name',
      key: 'pat_name',
      title: FieldLabels.PATIENT_NAME,
      render: (text, record, openReportEditor, openViewer, openRadiantViewer, enableRadiantViewer) => {
        const currentlyBlocked = record.order_workflow?.ow_reporting_blocked === true;
        const isEmergency = record.pacs_order?.po_priority?.toUpperCase() === 'EMERGENCY';
        const patientName = record.pacs_order?.patient?.pat_name;

        const buttonContent = (
          <Button
            className="ms-1auto d-flex align-items-center ph-0 col-pat-name"
            type="link"
            onClick={() => {
              if (!currentlyBlocked && openReportEditor) {
                openReportEditor(record);
                // openViewer(record);
              }
            }}
            style={{ padding: 0 }}
          >
            <Tooltip title={patientName}>
              <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
                maxWidth: isEmergency ? 'calc(100% - 22px)' : '100%'
              }}>
                {patientName}
              </span>
            </Tooltip>
            {isEmergency && (
              <Tooltip title="Emergency Case">
                <AlertOutlined
                  style={{
                    color: '#FF4D4F',
                    marginLeft: '6px',
                    fontSize: '14px',
                    animation: 'pulse 2s ease-in-out infinite'
                  }}
                />
              </Tooltip>
            )}
          </Button>
        );

        // Only wrap with Dropdown if Radiant Viewer is enabled
        if (enableRadiantViewer && openRadiantViewer) {
          return (
            <div>
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      key="radiant"
                      onClick={(e) => {
                        e.domEvent.stopPropagation();
                        const studyUID = record?.ps_study_uid;
                        if (studyUID) {
                          openRadiantViewer(studyUID);
                        }
                      }}
                    >
                      Open in Radiant Viewer
                    </Menu.Item>
                  </Menu>
                }
                trigger={['contextMenu']}
              >
                {buttonContent}
              </Dropdown>
            </div>
          );
        }

        // Return button without context menu if Radiant Viewer is disabled
        return <div>{buttonContent}</div>;
      },
      width: 250,
    },
    {
      dataIndex: 'modality',
      key: 'modality',
      title: FieldLabels.MODALITY,
      width: 100,
      render: (text, rec) => (
        <span
          style={{
            background: '#F5F5F5',
            border: '1px solid #EAEEF2',
            height: '24px',
            borderRadius: '4px',
            padding: '2px 8px',
            display: 'inline-block',
            lineHeight: '20px',
          }}
        >
          {rec?.pacs_order?.po_modality}
        </span>
      ),
    },
    {
      dataIndex: 'po_body_part',
      key: 'body_part',
      title: FieldLabels.BODY_PART,
      width: 120,
      render: (text, rec) => {
        const bodyPart = rec?.pacs_order?.po_body_part;
        return (
          <Tooltip title={bodyPart}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {bodyPart}
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'po_diag_desc',
      title: FieldLabels.STUDY_DESCRIPTION,
      key: 'diag_desc',
      width: 200,
      render: (txt, rec) => {
        const studyDesc = rec?.pacs_order?.po_diag_desc;
        const priority = rec.pacs_order?.po_priority;

        return (
          <Tooltip title={`${studyDesc}${priority ? ` (Priority: ${priority})` : ''}`}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {studyDesc}
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'ps_series_count',
      title: FieldLabels.SERIES_COUNT,
      key: 'ps_series_count',
      width: 105,
      render: (txt, rec) => {
        const ps_series_count = rec?.ps_series_count || 0;
        const ps_image_count = rec?.ps_image_count || 0;
        const isIngesting = !rec?.is_study_ingestion_complete;
        return (
          <Tooltip
            title={
              isIngesting
                ? 'Images are still being received from modality'
                : `Series: ${ps_series_count}, Images: ${ps_image_count}`
            }
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 6,
                width: '100%',
              }}
            >
              <span>
                {ps_series_count} / {ps_image_count}
              </span>

              {/* {isIngesting ? (
                <HourglassOutlined
                  spin
                  style={{ fontSize: 14, color: '#1890ff' }}
                />
              ) : (
                <CheckCircleOutlined style={{ fontSize: 14, color: '#52c41a' }} />
              )}  */}
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'po_ord_no',
      key: 'po_ord_no',
      title: FieldLabels.ORDER_NUMBER,
      width: 100,
      render: (text, rec) => rec?.pacs_order?.po_his_ord_no,
    },
    {
      dataIndex: 'po_acc_no',
      key: 'acc_no',
      title: FieldLabels.ACCESSION_NUMBER,
      width: 120,
      render: (text, rec) => {
        const accNo = rec?.pacs_order?.po_acc_no;
        return (
          <Tooltip title={accNo}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {accNo}
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'po_pat_age',
      key: 'pat_age',
      title: FieldLabels.PATIENT_AGE,
      width: 100,
      render: (val, record) => {
        const calculatedAge = calculateExactAge(record.pacs_order?.patient?.pat_dob);
        if (calculatedAge) return calculatedAge;
        const patAge = record.pacs_order?.patient?.pat_age;
        const match = String(patAge ?? '').match(/^(\d{1,3})Y$/i);
        return match ? `${Number(match[1])} years` : patAge;
      },
    },
    {
      dataIndex: 'po_pat_sex',
      key: 'pat_sex',
      title: FieldLabels.PATIENT_SEX,
      width: 100,
      render: (text, rec) => rec?.pacs_order?.patient?.pat_sex,
    },
    {
      dataIndex: 'po_ref_doc',
      key: 'ref_doc',
      title: FieldLabels.REFERRING_DOCTOR,
      width: 175,
      render: (text, rec) => {
        const refDoc = rec?.pacs_order?.po_ref_doc;
        return (
          <Tooltip title={refDoc}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                textAlign:refDoc?'left':'center',
              }}
            >
              {refDoc||'-'}
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'ps_study_dt_tm',
      key: 'study_dt_tm',
      title: FieldLabels.SCAN_DATE,
      render: val => (
        <span>{val ? moment(val).utcOffset('+05:30').format('Do MMM YYYY, hh:mmA') : '-'}</span>
      ),
      width: 200,
    },
    {
      dataIndex: 'ps_scan_received_at',
      key: 'scan_received_at',
      title: FieldLabels.RECEIVED_DATE,
      width: 200,
      render: val => (
        <span>{val ? moment(val).utcOffset('+05:30').format('Do MMM YYYY, hh:mmA') : '-'}</span>
      ),
    },
    {
      dataIndex: 'po_status',
      key: 'status',
      title: FieldLabels.STATUS,
      fixed: 'right',
      className: 'first-sticky-column',
      render: (text, record) => {
        const pacs_status = record?.order_workflow?.ow_pacs_status;
        return (
          <Tag
            className="custom-status-tag"
            style={getStatusStyle(pacs_status)}
          >
            {pacs_status?.replaceAll('_', ' ')}
          </Tag>
        );
      },
      width: 130,
    },
  ];
};

/**
 * PACS-specific columns (not in worklist)
 */
export const getPacsSpecificColumns = () => {
  const userDetails = getUserDetails();

  return [
    {
      dataIndex: 'ps_sending_aet',
      key: 'sending_aet',
      title: FieldLabels.AE_TITLE,
      width: 120,
      hidden: userDetails?.user_type === 'technician',
    },
    {
      dataIndex: '',
      key: 'reported_by',
      title: FieldLabels.REPORTED_BY,
      width: 150,
      hidden: userDetails?.user_type === 'technician',
      render: (text, rec) => {
        const user_name = rec?.reportedBy || '';
        return (
          <Tooltip title={user_name}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {user_name}
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: 'po_signed_by',
      key: 'signed_by',
      title: FieldLabels.SIGNED_BY,
      render: (text, record) => {
        const user_name = record?.signedBy || '';
        return (
          <Tooltip title={user_name}>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {user_name}
            </div>
          </Tooltip>
        );
      },
      width: 150,
      hidden: userDetails?.user_type === 'technician',
    },
    {
      dataIndex: 'assigned_to',
      key: 'assigned_to_tech',
      title: FieldLabels.TECHNICIAN,
      render: (text, record, ind, assignToSelfTechnician) => {
        return (
          <>
            {record?.order_workflow?.ow_assigned_technician ? (
              <span>{record?.order_workflow?.ow_assigned_technician}</span>
            ) : (
              <Popconfirm
                title="Assign to yourself?"
                onConfirm={() => assignToSelfTechnician && assignToSelfTechnician(record, ind)}
                okText="Yes"
                cancelText="No"
              >
                <Button>Self Assign</Button>
              </Popconfirm>
            )}
          </>
        );
      },
      width: 150,
      hidden: userDetails?.user_type !== 'technician',
    },
    {
      dataIndex: 'assigned_radiologist',
      key: 'assigned_radiologist',
      title: FieldLabels.ASSIGNED_RADIOLOGIST,
      render: (text, record, ind, handleRadiologistAssignment, userList) => {
        const assignedRadId = record?.order_workflow?.ow_assigned_rad_id;
        const canAssign = hasPermission(Permissions.ASSIGN_CASES);

        if (canAssign) {
          // Show dropdown for users with ASSIGN_CASES permission
          return (
            <Select
              value={assignedRadId || undefined}
              placeholder="Unassigned"
              allowClear
              style={{ width: '100%', minWidth: '150px' }}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={userList
                ?.filter(u => u.user_type === 'radiologist')
                .map(user => ({
                  label: user.user_fullname,
                  value: user.username,
                }))}
              onChange={value =>
                handleRadiologistAssignment && handleRadiologistAssignment(record, value, ind)
              }
              onClick={e => e.stopPropagation()}
            />
          );
        } else {
          // Show plain text for users without permission
          return <span>{assignedRadId || '-'}</span>;
        }
      },
      width: 180,
      hidden: false,
    },
  ];
};
